import React, { useContext, useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import handleErrorMessage from 'utils/handleErrorMessage';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  Button,
  Avatar,
  Checkbox,
  Paper,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useStateValue } from 'context/store';
import { I18nContext } from 'translations';
import { ROLES_COMPANY, SERVICE_TYPE_USER_TEXT } from 'utils/constants';
import { patch } from 'api/requests';
import { urlBlockCancellation } from 'api/index';
import EmptyData from '../../EmptyData';
import styles from '../../style';

const headerCellStyle = {
  fontSize: '0.6rem',
  fontWeight: 800,
  color: '#999',
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ebebeb',
  padding: '5px 2px 0px 2px',
  lineHeight: '1rem',
};

const cellStyle = { padding: '8px 12px' };

const actionBtnBase = {
  borderRadius: 8,
  textTransform: 'none',
  fontSize: '0.7rem',
  padding: '3px 9px',
  marginRight: 5,
};

const UsersTable = ({
  classes,
  data,
  editUser,
  removeUser,
  getUsers,
  recordsPerPage,
  usersPerGroup,
  btnChoose,
  selectUser,
  toggleActiveUser
}) => {
  const [currentPage, setCurrentPage] = useState(data.page);
  const [{ checkin: { checkin } }] = useStateValue();
  const { translate } = useContext(I18nContext);

  useEffect(() => {
    setCurrentPage(data.page);
  }, [data]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    getUsers(newPage);
  };

  const handleBlockCancellation = async (userId, checked) => {
    try {
      const response = await patch(urlBlockCancellation(userId), { block_cancellation: checked });
      getUsers(currentPage);
      return response.data.mssg;
    } catch (error) {
      return { error: true, message: handleErrorMessage(error) };
    }
  };

  const getInitials = (name) =>
    name.split(' ').map(x => x.charAt(0)).join('').substr(0, 2).toUpperCase();

  const getServiceText = (groups) => {
    if (!groups) return '-';
    return groups
      .filter(t => t.is_enabled && SERVICE_TYPE_USER_TEXT[t.requestable_service_type_group_cd])
      .map(t => translate(SERVICE_TYPE_USER_TEXT[t.requestable_service_type_group_cd]))
      .join(' / ') || '-';
  };

  if (!data || data.data.length === 0) {
    return <EmptyData message={translate('thereIsNoData')} />;
  }

  return (
    <>
      <Paper elevation={1} style={{ borderRadius: 12, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ ...headerCellStyle, width: 56 }} />
                <TableCell style={headerCellStyle}>{translate('app.settings.users.table.user')}</TableCell>
                <TableCell style={headerCellStyle}>{translate('app.settings.users.table.role')}</TableCell>
                <TableCell style={headerCellStyle}>{translate('app.settings.users.table.services')}</TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 285 }}>{translate('app.settings.users.table.actions')}</TableCell>
                <TableCell style={{ ...headerCellStyle, textAlign: 'center' }}>{translate('app.settings.users.table.cancelationLock')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.data.map(user => (
                <TableRow
                  hover
                  key={user._id}
                  style={{ opacity: user.active_passenger_in_company ? 1 : 0.6 }}
                >
                  <TableCell style={{ ...cellStyle, width: 56, paddingLeft: 16 }}>
                    {user.photo_url ? (
                      <Avatar alt={user.name} src={user.photo_url} style={{ width: 32, height: 32 }} />
                    ) : (
                      <Avatar style={{ width: 32, height: 32, backgroundColor: '#EDE7F6', color: '#6d28d9', fontSize: '0.65rem', fontWeight: 700 }}>
                        {getInitials(user.name)}
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell style={cellStyle}>
                    <Typography style={{ fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.2, margin: 0 }}>{user.name}</Typography>
                    <Typography style={{ fontSize: '0.63rem', color: '#888', lineHeight: 1.2, margin: 0 }}>{user.email}</Typography>
                  </TableCell>
                  <TableCell style={{ ...cellStyle, fontSize: '0.72rem' }}>
                    {ROLES_COMPANY[user.role_id] ? translate(ROLES_COMPANY[user.role_id]) : '-'}
                  </TableCell>
                  <TableCell style={{ ...cellStyle, fontSize: '0.72rem' }}>
                    {getServiceText(user.requestable_service_groups)}
                  </TableCell>
                  {!usersPerGroup && (
                    <TableCell style={cellStyle}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon style={{ fontSize: 13 }} />}
                        onClick={() => editUser(user)}
                        style={{ ...actionBtnBase, backgroundColor: '#7c3aed', color: '#fff' }}
                      >
                        {translate('app.settings.users.table.edit')}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DeleteIcon style={{ fontSize: 13 }} />}
                        onClick={() => removeUser(user)}
                        disabled={checkin && user._id === checkin.profile._id}
                        style={{ ...actionBtnBase, backgroundColor: '#ef4444', color: '#fff' }}
                      >
                        {translate('app.settings.users.table.remove')}
                      </Button>
                      {user.active_passenger_in_company ? (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<BlockIcon style={{ fontSize: 13 }} />}
                          onClick={() => toggleActiveUser(user, 0)}
                          style={{ ...actionBtnBase, backgroundColor: '#f59e0b', color: '#fff', marginRight: 0 }}
                        >
                          {translate('app.settings.users.inactivate')}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleOutlineIcon style={{ fontSize: 13 }} />}
                          onClick={() => toggleActiveUser(user, 1)}
                          style={{ ...actionBtnBase, backgroundColor: '#10b981', color: '#fff', marginRight: 0 }}
                        >
                          {translate('app.settings.users.activate')}
                        </Button>
                      )}
                    </TableCell>
                  )}
                  <TableCell style={{ ...cellStyle, textAlign: 'center' }}>
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={user.cant_cancel_bookings}
                      onChange={(event) => handleBlockCancellation(user._id, event.target.checked)}
                    />
                  </TableCell>
                  {btnChoose && (
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => selectUser(user)}
                        style={{ ...actionBtnBase, backgroundColor: '#7c3aed', color: '#fff' }}
                      >
                        {translate('choose')}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.pages * recordsPerPage}
          page={currentPage - 1}
          rowsPerPageOptions={[]}
          rowsPerPage={recordsPerPage}
          onChangePage={(event, newPage) => handleChangePage(event, newPage + 1)}
        />
      </Paper>
    </>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.data === nextProps.data;
}

export default withStyles(styles)(React.memo(UsersTable, areEqual));
